import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ACTIVE_EVENT_SLUG } from '@sentinel/utils'

// Setup default API base path matching Vite config base prefix
const API_BASE = (import.meta as any).env.VITE_API_BASE || '/api'

export interface EventConfig {
  registration_fields?: Array<{
    name: string
    label: string
    type: 'text' | 'number' | 'select'
    required?: boolean
    options?: string[]
  }>
  features?: {
    enable_quests?: boolean
    enable_referrals?: boolean
    enable_ticketing?: boolean
  }
  [key: string]: any
}

export interface Event {
  id: string
  slug: string
  name: string
  status: 'DRAFT' | 'OPEN' | 'CLOSED' | 'ARCHIVED'
  starts_at: string
  ends_at: string
  raffle_eligibility_threshold: number
  config: EventConfig | null
}

export interface Person {
  id: string
  whatsapp_number: string
  name: string
}

export interface EventRegistration {
  id: string
  event_id: string
  person_id: string
  referral_code: string
  wallet_balance: number
  shirt_size?: string
  shipping_address?: string
  postal_code?: string
  custom_fields?: Record<string, any>
  created_at: string
}

export const useParticipantStore = defineStore('participant', () => {
  const event = ref<Event | null>(null)
  const isRegistrationOpen = ref(true)
  const registration = ref<EventRegistration | null>(null)
  const person = ref<Person | null>(null)
  const token = ref<string | null>(localStorage.getItem('sentinel_session_token'))
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!token.value && !!registration.value)

  function clearError() {
    error.value = null
  }

  // Fetch only registration open status
  async function checkRegistrationStatus(slug: string) {
    try {
      const res = await fetch(`${API_BASE}/events/${slug}/settings/is_registration_open`)
      if (res.ok) {
        const data = await res.json()
        isRegistrationOpen.value = data.value !== false
      }
    } catch (e) {
      console.warn('Failed to fetch registration settings', e)
    }
  }

  // Load the active event using the URL slug or fallback constant
  async function loadEventContext(slug?: string) {
    loading.value = true
    error.value = null
    const targetSlug = slug || ACTIVE_EVENT_SLUG
    try {
      await checkRegistrationStatus(targetSlug)
      const res = await fetch(`${API_BASE}/events/${targetSlug}`)
      if (!res.ok) {
        throw new Error('Failed to resolve event information')
      }
      const data = await res.json()
      event.value = data
      return data
    } catch (err: any) {
      error.value = err.message || 'Error loading event context'
      return null
    } finally {
      loading.value = false
    }
  }

  // Register participant POST /api/events/:slug/registrations
  async function register(payload: {
    whatsapp_number: string
    name: string
    shirt_size?: string
    shipping_address?: string
    postal_code?: string
    custom_fields?: Record<string, any>
    referred_by_code?: string
  }) {
    if (!event.value) {
      error.value = 'No active event context loaded'
      return null
    }

    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/events/${event.value.slug}/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const data = await res.json()
      if (!res.ok) {
        throw new Error(data.error || 'Registration failed')
      }

      // If registered successfully, let's keep details or suggest login
      return data
    } catch (err: any) {
      error.value = err.message || 'Registration failed'
      return null
    } finally {
      loading.value = false
    }
  }

  // Send OTP POST /api/events/:slug/auth/otp
  async function sendOtp(whatsappNumber: string) {
    if (!event.value) {
      error.value = 'No active event context loaded'
      return false
    }

    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/events/${event.value.slug}/auth/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsapp_number: whatsappNumber }),
      })

      const data = await res.json()
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send OTP')
      }

      return true
    } catch (err: any) {
      error.value = err.message || 'Failed to send OTP'
      return false
    } finally {
      loading.value = false
    }
  }

  // Verify OTP POST /api/events/:slug/auth/verify
  async function verifyOtp(whatsappNumber: string, otp: string) {
    if (!event.value) {
      error.value = 'No active event context loaded'
      return false
    }

    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/events/${event.value.slug}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsapp_number: whatsappNumber, otp }),
      })

      const data = await res.json()
      if (!res.ok) {
        throw new Error(data.error || 'Invalid OTP code')
      }

      // Store token
      token.value = data.token
      localStorage.setItem('sentinel_session_token', data.token)

      // Store session details
      registration.value = data.registration
      person.value = data.person

      return true
    } catch (err: any) {
      error.value = err.message || 'Verification failed'
      return false
    } finally {
      loading.value = false
    }
  }

  // Fetch participant session details using current token
  async function fetchSessionProfile() {
    if (!token.value || !event.value) return false
    
    loading.value = true
    try {
      const res = await fetch(`${API_BASE}/events/${event.value.slug}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token.value}` },
      })
      if (!res.ok) {
        // Token might be expired or invalid
        logout()
        return false
      }
      const data = await res.json()
      registration.value = data.registration
      person.value = data.person
      return true
    } catch (err) {
      logout()
      return false
    } finally {
      loading.value = false
    }
  }

  function logout() {
    token.value = null
    registration.value = null
    person.value = null
    localStorage.removeItem('sentinel_session_token')
  }

  return {
    event,
    isRegistrationOpen,
    registration,
    person,
    token,
    loading,
    error,
    isAuthenticated,
    clearError,
    loadEventContext,
    checkRegistrationStatus,
    register,
    sendOtp,
    verifyOtp,
    fetchSessionProfile,
    logout,
  }
})
